import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Modal,
  Typography,
  Divider,
  Avatar,
  Switch,
  Tooltip,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { PersonOutline } from "@mui/icons-material";
import {
  InfoStatusType,
  User,
} from "../../../../../services/api/types/general/user";
import { formatDateOnly } from "../../../../../utils/helpers";
import { editUserAssignmentStatus } from "../../../../../services/api/apiClient";
import { AssignmentStatus } from "../../../../../services/api/types/userAssignment/types";
import { useToast } from "../../../../../context/ToastContext";
import { brand, gray, warning } from "../../../../../theme/themePrimitives";
import { extractErrorMessage } from "../../../../../utils/errorUtils";

interface UserStatusModalProps {
  openUserStatusModal: boolean;
  setOpenUserStatusModal: React.Dispatch<React.SetStateAction<boolean>>;
  users: User[];
  portalUid?: string;
  assignmentUid?: string;
  assignmentActive?: boolean;
  onUsersUpdated?: (updatedUsers: User[]) => void;
  onRefresh?: () => void;
  emptyMessage?: string;
}

export const UserStatusModal: React.FC<UserStatusModalProps> = ({
  openUserStatusModal,
  setOpenUserStatusModal,
  users,
  portalUid,
  assignmentUid,
  assignmentActive = true,
  onUsersUpdated,
  onRefresh,
  emptyMessage = "No users available",
}) => {
  const { showToast } = useToast();
  const [userStatuses, setUserStatuses] = useState<Record<string, boolean>>({});
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    const initialStatuses: Record<string, boolean> = {};
    users.forEach((user) => {
      const userUid = user.UserPortalUid;
      initialStatuses[userUid] = user.status === InfoStatusType.ACTIVE;
    });
    setUserStatuses(initialStatuses);
  }, [users]);

  const groupStatus = useMemo<"active" | "inactive" | "mixed">(() => {
    if (users.length === 0) return "inactive";

    const checkedCount = users.filter((user) => {
      const uid = user.UserPortalUid;
      return userStatuses[uid] ?? user.status === InfoStatusType.ACTIVE;
    }).length;

    if (checkedCount === users.length) return "active";
    if (checkedCount === 0) return "inactive";
    return "mixed";
  }, [users, userStatuses]);

  const updateUserStatuses = useCallback(
    async (
      allUsersWithStatuses: Array<{ userUid: string; status: boolean }>,
    ) => {
      if (!portalUid || !assignmentUid || isUpdatingRef.current) {
        return;
      }

      isUpdatingRef.current = true;

      try {
        // Group users by status
        const activeUserPortalUids = allUsersWithStatuses
          .filter((u) => u.status)
          .map((u) => u.userUid);
        const inactiveUserPortalUids = allUsersWithStatuses
          .filter((u) => !u.status)
          .map((u) => u.userUid);

        // Update users via API
        const updatePromises = [];
        if (activeUserPortalUids.length > 0) {
          updatePromises.push(
            editUserAssignmentStatus(portalUid, {
              assignments: [
                {
                  portalAssignmentUid: assignmentUid,
                  users: activeUserPortalUids,
                },
              ],
              status: AssignmentStatus.ACTIVE,
            }),
          );
        }
        if (inactiveUserPortalUids.length > 0) {
          updatePromises.push(
            editUserAssignmentStatus(portalUid, {
              assignments: [
                {
                  portalAssignmentUid: assignmentUid,
                  users: inactiveUserPortalUids,
                },
              ],
              status: AssignmentStatus.INACTIVE,
            }),
          );
        }
        await Promise.all(updatePromises);

        // Update users with new statuses
        const getStatus = (uid: string) => {
          const userStatus = allUsersWithStatuses.find(
            (u) => u.userUid === uid,
          );
          return userStatus?.status
            ? InfoStatusType.ACTIVE
            : InfoStatusType.INACTIVE;
        };

        const updatedUsers = users.map((user) => {
          const uid = getUserPortalUid(user);
          return { ...user, status: getStatus(uid) };
        });

        onUsersUpdated?.(updatedUsers);
        onRefresh?.();
        showToast("User status updated successfully", "success");
      } catch (error: any) {
        showToast(
          extractErrorMessage(error, "Failed to update user status"),
          "error",
        );
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [portalUid, assignmentUid, users, onUsersUpdated, onRefresh, showToast],
  );

  const handleStatusChange = useCallback(
    (userUid: string, checked: boolean) => {
      if (!assignmentActive) {
        return;
      }

      const updatedStatuses = { ...userStatuses, [userUid]: checked };
      setUserStatuses(updatedStatuses);

      // Get all users with their updated statuses
      const allUsersWithStatuses = users.map((user) => {
        const uid = getUserPortalUid(user);
        return {
          userUid: uid,
          status: updatedStatuses[uid] ?? user.status === InfoStatusType.ACTIVE,
        };
      });

      updateUserStatuses(allUsersWithStatuses);
    },
    [assignmentActive, users, userStatuses, updateUserStatuses],
  );

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      if (!assignmentActive) {
        return;
      }

      const userUids = users.map((user) => user.UserPortalUid);

      const updatedStatuses: Record<string, boolean> = {};
      userUids.forEach((uid) => {
        updatedStatuses[uid] = checked;
      });
      setUserStatuses((prev) => ({ ...prev, ...updatedStatuses }));

      // Get all users with their updated statuses
      const allUsersWithStatuses = users.map((user) => {
        const uid = getUserPortalUid(user);
        return {
          userUid: uid,
          status: updatedStatuses[uid] ?? checked,
        };
      });

      updateUserStatuses(allUsersWithStatuses);
    },
    [assignmentActive, users, updateUserStatuses],
  );

  const getUserPortalUid = (user: User) => user.UserPortalUid;
  const getUserStatus = (user: User) =>
    userStatuses[getUserPortalUid(user)] ??
    user.status === InfoStatusType.ACTIVE;

  return (
    <Modal
      open={openUserStatusModal}
      onClose={() => setOpenUserStatusModal(false)}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "60vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          width: "50%",
          maxWidth: "600px",
        }}
      >
        <Box sx={{ padding: "16px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">Users preview</Typography>
            <IconButton onClick={() => setOpenUserStatusModal(false)}>
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>

          <Divider sx={{ my: 1 }} />
        </Box>
        <Box>
          {users.length === 0 ? (
            <Typography>{emptyMessage}</Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 16px",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={600}>
                    User
                  </Typography>
                </Box>
                <Box sx={{ width: "120px", textAlign: "start" }}>
                  <Typography variant="body1" fontWeight={600}>
                    Due date
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "999px",
                    backgroundColor: gray[100],
                    padding: "2px",
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    disabled={!assignmentActive}
                    onClick={() => handleToggleAll(true)}
                    sx={{
                      border: "none",
                      cursor: assignmentActive ? "pointer" : "not-allowed",
                      borderRadius: "999px",
                      padding: "4px 14px",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      fontFamily: "inherit",
                      opacity: assignmentActive ? 1 : 0.5,
                      backgroundColor:
                        groupStatus === "active" ? brand[600] : "transparent",
                      color: groupStatus === "active" ? "#fff" : gray[600],
                    }}
                  >
                    Active
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      padding: "4px 14px",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: groupStatus === "mixed" ? warning[600] : gray[400],
                    }}
                  >
                    Mixed
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    disabled={!assignmentActive}
                    onClick={() => handleToggleAll(false)}
                    sx={{
                      border: "none",
                      cursor: assignmentActive ? "pointer" : "not-allowed",
                      borderRadius: "999px",
                      padding: "4px 14px",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      fontFamily: "inherit",
                      opacity: assignmentActive ? 1 : 0.5,
                      backgroundColor:
                        groupStatus === "inactive" ? "#fff" : "transparent",
                      boxShadow:
                        groupStatus === "inactive"
                          ? "0px 1px 2px rgba(16,24,40,0.06)"
                          : "none",
                      color: gray[700],
                    }}
                  >
                    Inactive
                  </Box>
                </Box>
              </Box>

              {users.map((user, index) => {
                const userUid = getUserPortalUid(user);
                return (
                  <Box
                    key={userUid}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      py: 1,
                      borderBottom:
                        index < users.length - 1 ? "1px solid #EAECF0" : "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                        mr: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: "#F2F4F7",
                          padding: "6px",
                          width: "32px",
                          height: "32px",
                          mr: 1.5,
                        }}
                      >
                        <PersonOutline
                          sx={{ height: "24px", color: "#98A2B3" }}
                        />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                          }}
                        >
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        width: "120px",
                      }}
                    >
                      <Typography variant="body2">
                        {user.dueDate ? formatDateOnly(user.dueDate) : "-"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: "80px",
                        display: "flex",
                      }}
                    >
                      <Tooltip
                        title={
                          assignmentActive
                            ? ""
                            : "This assignment is inactive and cannot be activated for users"
                        }
                      >
                        <span>
                          <Switch
                            checked={getUserStatus(user)}
                            disabled={!assignmentActive}
                            onChange={(e) =>
                              handleStatusChange(userUid, e.target.checked)
                            }
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
